import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NotebookPen, Flame, Sparkles } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import { Badge } from "@/components/ui/badge";
import { NAV_CATEGORIES, type NavTool } from "@/data/navigation";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

/**
 * Landing page for the "Play" category - a hub listing every game.
 *
 * Reads tools from `navigation.ts` so adding a new game in one place
 * automatically surfaces it here, in the header dropdown, the footer,
 * and the /explore index. v1 ships with two games (Element Battle, Chem
 * Duel) and a "More games coming" placeholder so the surface area looks
 * intentional rather than thin.
 */

const COMING_SOON: Array<{ label: string; description: string }> = [];

/**
 * Per-game metadata for grouping and sorting on the Play hub.
 *
 * - `group` clusters games by what the player actually does so the hub
 *   reads as a curated arcade rather than a flat list.
 * - `popularity` is a manual 0-100 score (higher = more played); update
 *   when analytics suggest a ranking shift.
 * - `addedOn` is the launch date used for the "Recent" sort.
 *
 * Keyed by `path` so adding/removing entries in `navigation.ts` is the
 * source of truth - entries here are optional and fall back to defaults.
 */
type GameGroup = "Daily" | "Trivia" | "Speed drills" | "Puzzles" | "Identify";

const GAME_META: Record<
  string,
  { group: GameGroup; popularity: number; addedOn: string }
> = {
  "/daily":                       { group: "Daily",       popularity: 95, addedOn: "2026-04-20" },
  "/play/chem-doku":              { group: "Puzzles",     popularity: 80, addedOn: "2026-04-15" },
  "/play/element-battle":         { group: "Trivia",      popularity: 90, addedOn: "2025-09-01" },
  "/play/chem-duel":              { group: "Trivia",      popularity: 85, addedOn: "2025-09-10" },
  "/play/lab-safety-showdown":    { group: "Trivia",      popularity: 60, addedOn: "2025-12-05" },
  "/play/speed-quiz":             { group: "Speed drills", popularity: 75, addedOn: "2025-10-01" },
  "/play/reaction-race":          { group: "Speed drills", popularity: 70, addedOn: "2025-10-15" },
  "/play/periodic-sprint":        { group: "Speed drills", popularity: 72, addedOn: "2026-01-12" },
  "/play/electron-config-sprint": { group: "Speed drills", popularity: 55, addedOn: "2026-03-02" },
  "/play/bond-type-snap":         { group: "Speed drills", popularity: 58, addedOn: "2026-02-08" },
  "/play/glassware-id":           { group: "Identify",    popularity: 50, addedOn: "2025-11-20" },
  "/play/ph-sort":                { group: "Identify",    popularity: 48, addedOn: "2025-12-18" },
};

const GROUP_ORDER: GameGroup[] = ["Daily", "Puzzles", "Trivia", "Speed drills", "Identify"];

const DEFAULT_META = { group: "Trivia" as GameGroup, popularity: 0, addedOn: "2025-01-01" };

/** "Recent" badge for games launched within ~60 days of today. */
function isRecent(addedOn: string): boolean {
  const ageDays = (Date.now() - new Date(addedOn).getTime()) / 86_400_000;
  return ageDays <= 60;
}

type SortMode = "popular" | "recent";

export default function PlayHub() {
  const playCategory = NAV_CATEGORIES.find((c) => c.key === "play");
  const games = playCategory?.tools ?? [];
  const [sortMode, setSortMode] = useState<SortMode>("popular");

  const grouped = useMemo(() => {
    const buckets = new Map<GameGroup, NavTool[]>();
    for (const g of games) {
      const meta = GAME_META[g.path] ?? DEFAULT_META;
      const list = buckets.get(meta.group) ?? [];
      list.push(g);
      buckets.set(meta.group, list);
    }
    for (const [, list] of buckets) {
      list.sort((a, b) => {
        const ma = GAME_META[a.path] ?? DEFAULT_META;
        const mb = GAME_META[b.path] ?? DEFAULT_META;
        if (sortMode === "popular") return mb.popularity - ma.popularity;
        return new Date(mb.addedOn).getTime() - new Date(ma.addedOn).getTime();
      });
    }
    return GROUP_ORDER
      .map((group) => ({ group, items: buckets.get(group) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [games, sortMode]);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemistry Games — ElementPad" description="Play chemistry mini-games: element battle, periodic sprint, lab safety, pH sort, bond type snap, and more." path="/play" />
      <AppHeader />
      <Main className="max-w-[1200px] mx-auto px-4 py-8">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <h1 className="text-2xl font-bold text-foreground mb-1">Play</h1>
            <p className="text-sm text-muted-foreground">
              Two-player chemistry games for the same device. Take turns, no accounts,
              no setup - just hand over the keyboard.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-label="Sort games"
            className="inline-flex rounded-lg border border-border bg-card p-1 text-xs"
          >
            {(
              [
                { id: "popular", label: "Popular", icon: Flame },
                { id: "recent", label: "Recent", icon: Sparkles },
              ] as const
            ).map(({ id, label, icon: Icon }) => {
              const active = sortMode === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSortMode(id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
                    active
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon size={12} aria-hidden="true" />
                  {label}
                </button>
              );
            })}
          </div>
        </header>

        {/* Live games - grouped by category, sorted by popularity or recency */}
        {grouped.map(({ group, items }) => (
          <section
            key={group}
            aria-labelledby={`group-${group}`}
            className="mb-8"
          >
            <div className="flex items-baseline justify-between gap-2 mb-3 pb-2 border-b border-border">
              <h2
                id={`group-${group}`}
                className="text-sm font-semibold text-foreground"
              >
                {group}
              </h2>
              <span className="text-[11px] text-muted-foreground">
                {items.length} {items.length === 1 ? "game" : "games"}
              </span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((g) => {
                const Icon = g.icon;
                const meta = GAME_META[g.path] ?? DEFAULT_META;
                const recent = isRecent(meta.addedOn);
                return (
                  <li key={g.path}>
                    <Link
                      to={g.path}
                      className="group flex items-start gap-4 p-5 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors h-full"
                    >
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Icon size={20} className="text-primary" aria-hidden="true" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="text-base font-semibold text-foreground leading-tight">
                            {g.label}
                          </span>
                          <span className="text-[10px] font-medium uppercase tracking-wider text-primary">
                            Play →
                          </span>
                        </span>
                        <span className="block text-sm text-muted-foreground leading-snug mb-2">
                          {g.description}
                        </span>
                        {recent && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0"
                          >
                            New
                          </Badge>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {/* Coming soon */}
        {COMING_SOON.length > 0 && (
          <section aria-labelledby="games-soon">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
              <NotebookPen size={14} className="text-muted-foreground" aria-hidden="true" />
              <h2 id="games-soon" className="text-sm font-semibold text-foreground">
                Coming soon
              </h2>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMING_SOON.map((g) => (
                <li
                  key={g.label}
                  className="p-4 rounded-lg border border-dashed border-border bg-muted/20"
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground">{g.label}</span>
                    <Badge variant="secondary" className="text-[10px]">
                      Soon
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">{g.description}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </Main>
    </div>
  );
}